/**
 * SplitOrderSettingsSection - split-order auto-accept rule (BLU-563 R3)
 *
 * Configures the org-wide tag rule for Shopify split orders: new split orders
 * whose Shopify tags match any tag here are ACCEPTED automatically on arrival
 * (each location produces its own items; no Split Orders issue is raised).
 * Applies to new orders as they arrive; already-held splits are untouched.
 *
 * Maps 1:1 to prod_settings.auto_accept_split_tags, consumed by order-sync's
 * split-detect hook. Backed by `productionSettings.getSplitOrderSettings` /
 * `updateSplitOrderSettings`; the tag picker is the shadcn Command-in-Popover
 * combobox (mirrors OrderQueueSettingsSection's searchOrderTags picker —
 * keyboard navigable, cmdk-managed ARIA, no blur-timeout hack), extended with
 * a "creatable" first row: Enter adds the EXACT typed value; live suggestions
 * (productionSettings.searchOrderTags — distinct tags from real orders) add
 * only by explicit selection (click or arrow+Enter).
 * Load + save + dirty-state mirror BenchRoutingSection (savedRef baseline,
 * toast on success/error, refetch after save).
 */
import { useEffect, useRef, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Plus, X, Info, Loader2, Search } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useToast } from '@/hooks/use-toast';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

// Mirror the backend schema bounds (updateSplitOrderSettings: max 25 tags, 60 chars).
const MAX_TAGS = 25;
const MAX_TAG_LENGTH = 60;

export function SplitOrderSettingsSection() {
  const { toast } = useToast();

  const [tags, setTags] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  // Dirty-state baseline (mirrors BenchRoutingSection's savedRef).
  const [loaded, setLoaded] = useState(false);
  const savedRef = useRef<string[]>([]);

  const settingsQuery = trpc.productionSettings.getSplitOrderSettings.useQuery();
  const updateMutation = trpc.productionSettings.updateSplitOrderSettings.useMutation();

  useEffect(() => {
    if (settingsQuery.data && !loaded) {
      setTags(settingsQuery.data.auto_accept_split_tags);
      savedRef.current = settingsQuery.data.auto_accept_split_tags;
      setLoaded(true);
    }
  }, [settingsQuery.data, loaded]);

  // Realtime tag search off the org's actual order tags (300ms debounce; the
  // RPC unnests prod_orders.tags, so suggestions are tags that really occur).
  const debouncedDraft = useDebouncedValue(draft.trim(), 300);
  const tagSearch = trpc.productionSettings.searchOrderTags.useQuery(
    { query: debouncedDraft },
    { enabled: pickerOpen && debouncedDraft.length > 0, staleTime: 30_000 }
  );
  const lowerTags = new Set(tags.map((t) => t.toLowerCase()));
  const suggestions = (tagSearch.data ?? []).filter((s) => !lowerTags.has(s.toLowerCase()));

  const addTag = (raw: string) => {
    const tag = raw.trim();
    if (!tag || tag.length > MAX_TAG_LENGTH) return;
    if (tags.length >= MAX_TAGS) return;
    if (lowerTags.has(tag.toLowerCase())) return;
    setTags((prev) => [...prev, tag]);
    setDraft('');
  };

  // The "creatable" first row: Enter in the CommandInput adds the EXACT typed
  // value (cmdk auto-highlights the first item, so plain Enter lands here —
  // never on a suggestion; suggestions require click or arrow+Enter). Hidden
  // when the trimmed draft is empty, over-length, or already a chip.
  const trimmedDraft = draft.trim();
  const canAddDraft =
    trimmedDraft.length > 0 &&
    trimmedDraft.length <= MAX_TAG_LENGTH &&
    !lowerTags.has(trimmedDraft.toLowerCase());
  const removeTag = (tag: string) => setTags((prev) => prev.filter((t) => t !== tag));

  const hasUnsavedChanges = JSON.stringify(tags) !== JSON.stringify(savedRef.current);
  const enabled = tags.length > 0;

  const handleSave = () => {
    updateMutation.mutate(
      { auto_accept_split_tags: tags },
      {
        onSuccess: () => {
          toast({
            title: 'Split order settings saved',
            description:
              tags.length > 0
                ? 'New split orders matching these tags will be accepted automatically.'
                : 'Auto-accept is off. All new split orders will be held for manual resolution.',
            duration: 3000,
          });
          savedRef.current = tags;
          settingsQuery.refetch();
        },
        onError: (error) => {
          toast({
            title: 'Failed to save split order settings',
            description: error.message,
            variant: 'destructive',
            duration: 5000,
          });
        },
      }
    );
  };

  const busy = settingsQuery.isLoading || updateMutation.isPending;

  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <Label htmlFor="split-auto-accept-tags" className="text-base font-medium">
          Auto-accept split orders with these tags
        </Label>
        <p className="text-sm text-muted-foreground">
          New split orders whose Shopify tags match are accepted automatically: each location
          produces its own items and no issue is raised. Applies to new orders as they arrive.
          Leave empty to hold every split order for manual resolution.
        </p>
      </div>

      {/* Tag picker: Command-in-Popover combobox (mirrors OrderQueueSettings-
          Section's searchOrderTags picker), plus a creatable "Add <typed>"
          first row so Enter always adds the exact typed value. */}
      <div className="max-w-md space-y-2">
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              id="split-auto-accept-tags"
              variant="outline"
              role="combobox"
              aria-expanded={pickerOpen}
              disabled={busy || tags.length >= MAX_TAGS}
              className="h-11 w-full justify-start gap-2 font-normal text-muted-foreground"
            >
              <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
              Search or add order tags...
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Type to search or add a tag..."
                value={draft}
                onValueChange={setDraft}
                maxLength={MAX_TAG_LENGTH}
              />
              <CommandList>
                {canAddDraft && (
                  <CommandGroup>
                    <CommandItem
                      value={`__add__${trimmedDraft}`}
                      onSelect={() => addTag(trimmedDraft)}
                      className="cursor-pointer gap-2"
                    >
                      <Plus className="h-4 w-4 shrink-0" aria-hidden="true" />
                      Add &ldquo;{trimmedDraft}&rdquo;
                    </CommandItem>
                  </CommandGroup>
                )}
                {debouncedDraft.length === 0 ? (
                  <div className="py-4 text-center text-sm text-muted-foreground">
                    Type to search your order tags
                  </div>
                ) : tagSearch.isLoading ? (
                  <div className="flex items-center justify-center gap-2 py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
                    <span className="text-sm text-muted-foreground">Searching tags...</span>
                  </div>
                ) : suggestions.length === 0 ? (
                  <CommandEmpty>No matching order tags</CommandEmpty>
                ) : (
                  <CommandGroup heading="Matching order tags">
                    {suggestions.map((s) => (
                      <CommandItem
                        key={s}
                        value={s}
                        onSelect={() => addTag(s)}
                        className="cursor-pointer"
                      >
                        {s}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {tags.length >= MAX_TAGS && (
          <p className="text-sm text-muted-foreground">Maximum {MAX_TAGS} tags</p>
        )}
      </div>

      {/* Selected tags as removable chips. */}
      {tags.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No tags configured. Auto-accept is off.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full border border-threadheads-purple/30 bg-threadheads-purple/10 py-1 pl-3 pr-1 text-sm text-threadheads-purple"
            >
              {tag}
              {/* 32px min hit target — mirrors BenchRoutingSection's remove button. */}
              <button
                type="button"
                aria-label={`Remove tag ${tag}`}
                className="flex min-h-[32px] min-w-[32px] items-center justify-center rounded-full transition-colors hover:bg-threadheads-purple/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                onClick={() => removeTag(tag)}
                disabled={busy}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          {enabled
            ? 'Auto-accepted orders skip the Split Orders tab entirely. Each half is produced and shipped from its own location.'
            : 'When enabled, tagged split orders skip the Split Orders tab and each half is produced from its own location.'}{' '}
          Already-held split orders are not affected.
        </span>
      </div>

      <div className="flex items-center gap-3">
        <Button
          onClick={handleSave}
          disabled={busy || !hasUnsavedChanges}
          className="gap-1.5"
        >
          {updateMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Save split order settings
        </Button>
        {hasUnsavedChanges && !updateMutation.isPending && (
          <span className="text-sm text-amber-600">Unsaved changes</span>
        )}
      </div>
    </div>
  );
}
